import React, { useEffect, useState } from "react";

import { DashboardEditorProps } from "components/ComponentType";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { defaultLoadConf } from "components/dashboard/data-input-output-table/defaultConfigs";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { DatasetLoadingConf, DatasetPieProps } from "../interfaces";
import DataTableComponent from "./DataTableComponent";
import { defaultDatasetPieChartConfig } from "./pieChartDefaultConfigs";

interface PieChart {
  identifiers?: string[];
  chartName?: string;
}

function DatasetPieChartEditor({
  config,
  onConfigChange,
}: DashboardEditorProps<DatasetPieProps>) {
  let conf = config ?? defaultDatasetPieChartConfig;
  if (Object.keys(conf).length === 0) {
    conf = defaultDatasetPieChartConfig;
  }

  const [currentConfig, setCurrentConfig] = useState(conf);
  const [currentCenterTextConfig, setCurrentCenterTextConfig] = useState(
    conf.centerTextConfig
  );
  const [currentChartsArray, setCurrentChartsArray] = useState<PieChart[]>(
    conf.charts
  );
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([]);

  useEffect(() => {
    const tempIdentifiers = currentConfig.datasets?.map(
      (dataset) => dataset.identifier
    );
    if (tempIdentifiers) {
      setConfigPropertyState("identifiers", tempIdentifiers);
    }
  }, [currentConfig.datasets]);

  useEffect(() => {
    const initialSelectedDatasets = currentConfig.datasets?.map(
      (dataset) => dataset.identifier
    );
    setSelectedDatasets(initialSelectedDatasets ?? []);
    setConfigPropertyState("charts", currentConfig.charts);
  }, []);

  useEffect(() => {
    updateCharts("charts");
  }, [currentChartsArray]);

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  const handleLoadConf = (e: any): void => {
    const tempConfig = structuredClone(currentConfig);
    if (e.value) {
      tempConfig.loadConf = defaultLoadConf;
    } else {
      delete tempConfig.loadConf;
    }
    setCurrentConfig(tempConfig);
  };

 const handleLoadConfChange = (id: string, value: number) => {
  const tempConfig = structuredClone(currentConfig);
  const loadingConfig: DatasetLoadingConf | undefined = tempConfig.loadConf;
  loadingConfig[id as keyof DatasetPieProps] = Number.isNaN(value)
    ? value.toString().toUpperCase()
    : value;
  setCurrentConfig(tempConfig);
};

  const setConfigPropertyState = (
    property: keyof DatasetPieProps,
    value: any
  ) => {
    if (property === "charts") {
      const updatedCharts = (value || []).map(
        (chart: PieChart, index: number) => ({
          ...chart,
          chartName: chart.chartName || `Chart ${index + 1}`,
        })
      );
      setCurrentConfig((prevConfig) => ({
        ...prevConfig,
        [property]: updatedCharts,
      }));
    } else {
      setCurrentConfig((prevConfig) => ({
        ...prevConfig,
        [property]: value,
      }));
    }
  };

  const updateCharts = (property: keyof DatasetPieProps) => {
    setCurrentConfig((prevConfig) => ({
      ...prevConfig,
      [property]: currentChartsArray,
    }));
  };

  const handleConfigChange = (e: any): void => {
    setConfigPropertyState(
      e.target.id as keyof DatasetPieProps,
      e.target.value
    );
  };

  const setConfigPropertyStateForCenterTextConfig = (
    property: keyof DatasetPieProps,
    value: any
  ) => {
    const newConfig = { ...currentCenterTextConfig, [property]: value };
    setCurrentCenterTextConfig(newConfig);
    setConfigPropertyState("centerTextConfig", newConfig);
  };

  const handleConfigChangeForCenterTextConfig = (e: any): void => {
    setConfigPropertyStateForCenterTextConfig(
      e.target.id as keyof DatasetPieProps,
      e.target.value
    );
  };

  const handleAddChart = () => {
    const newChart: PieChart = {
      chartName: "",
      identifiers: [],
    };

    if (currentConfig.charts?.length === 0) {
      newChart.chartName = "Chart 1";
    } else {
      const lastChart: PieChart =
        currentConfig.charts[currentConfig.charts.length - 1];
      const lastChartNumber = parseInt(lastChart.chartName?.split(" ")[1] ?? "0", 10);
      const newChartNumber = lastChartNumber + 1;

      newChart.chartName = `Chart ${newChartNumber}`;
    }
    setCurrentConfig((prevConfig: any) => {
      const updatedCharts = [...prevConfig.charts, newChart];
      return { ...prevConfig, charts: updatedCharts };
    });
  };

  const handleDatasetChange = (
    index: number,
    selectedIdentifiers: string[]
  ): void => {
    const updatedCharts = [...currentConfig.charts];

    updatedCharts[index] = {
      ...updatedCharts[index],
      identifiers: selectedIdentifiers,
    };
    setCurrentConfig((prevConfig) => ({
      ...prevConfig,
      charts: updatedCharts,
    }));
    setSelectedDatasets(selectedIdentifiers);
  };

  return (
    <div>
      <div className="grid p-fluid w-full">
        <DataTableComponent
          charts={currentConfig.charts || []}
          onDataUpdate={setCurrentChartsArray}
          selectedDatasets={selectedDatasets}
          handleDatasetChange={(index, selectedIdentifiers) =>
            handleDatasetChange(index, selectedIdentifiers)
          }
          handleAddChart={handleAddChart}
        />
        <div className="col-2 flex flex-column mb-3">
          <label htmlFor="height">Height</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="height"
              value={currentConfig.height as number}
              onValueChange={handleConfigChange}
            />
            <span className="p-inputgroup-addon">px</span>
          </div>
        </div>
        <div className="col-2 flex flex-column mb-3">
          <label htmlFor="fontSize">Font Size</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="fontSize"
              value={currentConfig.centerTextConfig?.fontSize as number}
              onValueChange={handleConfigChangeForCenterTextConfig}
            />
            <span className="p-inputgroup-addon">px</span>
          </div>
        </div>

        <div className="col-2 flex flex-column mb-3">
          <label htmlFor="decimalPlaces">Decimal places</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="decimalPlaces"
              value={currentConfig.centerTextConfig?.decimalPlaces as number}
              onValueChange={handleConfigChangeForCenterTextConfig}
            />
          </div>
        </div>
        <div className="col-2 flex flex-column mb-3">
          <label htmlFor="chartsPerRow">Charts per row</label>
          <div className="p-inputgroup w-full">
            <InputNumber
              id="chartsPerRow"
              value={currentConfig.chartsPerRow as number}
              onValueChange={handleConfigChange}
            />
          </div>
        </div>
        <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="showDateSelection">Show Date Selection</label>
          <InputSwitch
            id="showDateSelection"
            checked={currentConfig.showDateSelection}
            onChange={handleConfigChange}
          />
        </div>
        <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="hideRefresh">Hide Refresh</label>
          <InputSwitch
            id="hideRefresh"
            checked={currentConfig.hideRefresh}
            onChange={handleConfigChange}
          />
        </div>
        <div className="gap-2 flex flex-column justify-content-center align-items-center">
          <label htmlFor="loadConf">Load Conf</label>
          <InputSwitch
            id="loadConf"
            checked={currentConfig.loadConf !== undefined}
            onChange={handleLoadConf}
          />
        </div>
        {currentConfig.loadConf !== undefined && (
          <>
            <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="lag">Lag</label>
              <div className="p-inputgroup">
                <InputNumber
                  id="lag"
                  value={currentConfig.loadConf.lag as number}
                  onValueChange={(e) =>
                    handleLoadConfChange(e.target.id, e.target.value as number)
                  }
                />
                <TimeUnitDropdown
                  id="lagUnit"
                  className="p-inputgroup-addon p-0"
                  value={currentConfig.loadConf.lagUnit}
                  onChange={(e) =>
                    handleLoadConfChange(e.target.id, e.target.value as number)
                  }
                />
              </div>
            </div>
            <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="lead">Lead</label>
              <div className="p-inputgroup">
                <InputNumber
                  id="lead"
                  value={currentConfig.loadConf.lead as number}
                  onValueChange={(e) =>
                    handleLoadConfChange(e.target.id, e.target.value as number)
                  }
                />
                <TimeUnitDropdown
                  id="leadUnit"
                  className="p-inputgroup-addon p-0"
                  value={currentConfig.loadConf.leadUnit}
                  onChange={(e) =>
                    handleLoadConfChange(e.target.id, e.target.value as number)
                  }
                />
              </div>
            </div>
            <div className="col-3 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="alignUnit">Align Unit</label>
              <TimeUnitDropdown
                id="alignUnit"
                className="w-full"
                value={
                  currentConfig.loadConf.alignUnit
                    ? currentConfig.loadConf.alignUnit
                    : ""
                }
                onChange={(e) =>
                  handleLoadConfChange(e.target.id, e.target.value as number)
                }
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DatasetPieChartEditor;
